import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useDebounced } from '#src/hooks/useDebounced.ts'

export interface UseUrlSearchInputOptions {
  // URL param the query round-trips through.
  param?: string
  debounceMs?: number
  /* `true` keeps typing out of the history stack. Surfaces that want each
   * query to be a back-navigable step pass `false`. */
  replace?: boolean
  /* Runs inside the same `setSearchParams` updater as the query write, and
   * only when the query actually changed, so a caller's own params (page
   * index, sort) are reset atomically with it. Two separate writes in one
   * event would not be: React Router rebuilds a functional update from the
   * render's closure, so the second would clobber the first.
   *
   * Held in a ref, so an inline arrow here does not re-run the effect. */
  onDebouncedChange?: (params: URLSearchParams) => void
}

export interface UseUrlSearchInputReturn {
  // Live input value. Bind straight to `ShortcutSearchInput`.
  searchInput: string
  setSearchInput: (value: string) => void
  // Settled value. Drive the URL write and the search index off this.
  debouncedSearch: string
}

/* Round-trips a search box through a URL param: seeded from the param on
 * mount, debounced on the way back out, and re-mirrored into the input when
 * the URL moves under it (back/forward navigation, a deep link).
 *
 * Input state is deliberately decoupled from the URL so typing never blocks
 * on a router update or a search-index rebuild.
 *
 * The two effects cannot ping-pong. The write-back depends only on
 * `debouncedSearch`, reading the current params off a ref rather than listing
 * them as a dependency, and skips the write entirely when the param already
 * matches. So a back-navigation that clears the param mirrors into the input
 * and settles there instead of being overwritten by the stale debounced
 * value. */
export function useUrlSearchInput({
  param = 'search',
  debounceMs = 300,
  replace = true,
  onDebouncedChange,
}: UseUrlSearchInputOptions = {}): UseUrlSearchInputReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSearch = searchParams.get(param) ?? ''
  const [searchInput, setSearchInput] = useState(urlSearch)
  const debouncedSearch = useDebounced(searchInput, debounceMs)

  /* Current params without making them an effect dependency — see the
   * ping-pong note above. */
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  /* `setSearchParams` is re-created whenever the params change, so depending
   * on it would re-run the write-back on every unrelated URL change — which on
   * a back-navigation means re-pushing the stale query and breaking the back
   * button. Pin it too, so the only trigger is the debounced value. */
  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams

  const onDebouncedChangeRef = useRef(onDebouncedChange)
  onDebouncedChangeRef.current = onDebouncedChange

  useEffect(() => {
    if ((searchParamsRef.current.get(param) ?? '') === debouncedSearch) {
      return
    }
    setSearchParamsRef.current(
      (prev) => {
        if (debouncedSearch) {
          prev.set(param, debouncedSearch)
        } else {
          prev.delete(param)
        }
        onDebouncedChangeRef.current?.(prev)
        return prev
      },
      { replace }
    )
  }, [debouncedSearch, param, replace])

  // Mirror URL → input on mount / back-nav.
  useEffect(() => {
    setSearchInput((prev) => (prev === urlSearch ? prev : urlSearch))
  }, [urlSearch])

  return { searchInput, setSearchInput, debouncedSearch }
}
